import { Button, CircularProgress, Skeleton, Typography } from '@mui/material';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import Box from '@mui/material/Box';
import type { FC, ReactNode } from 'react';
import { Suspense, useCallback, useEffect, useState } from 'react';
import {
  ConfirmationDialog,
  SelectApplications,
  SelectPlatform,
  SuspenseBoundary,
  TextField,
} from 'components/common';
import GridErrorBoundaryFallback from './GridErrorBoundaryFallback';
import SelectFacebookPage from './SelectFacebookPages';
import { Add, Remove } from '@mui/icons-material';
import SelectCreativeTexts from './SelectCreativeTexts';
import GlobalErrorBoundary from 'components/GlobalErrorBoundary';
import type { CreativeText, FacebookPage, FlowDetails } from '../types';
import { isEmpty } from 'lodash-es';
import DeleteFlow from './DeleteFlow';
import { useEnqueueSnackbar, useModal, useScrollToError } from 'hooks';
import { type AddFacebookPageResponse, type CreativeTextResponse, useAddFlow, useUpdateFlow } from '../api';
import {
  cabinetDatadogSources,
  createCabinetMutationTracking,
  trackCabinetSubmitAttempt,
} from 'lib/datadog';
import { addFlowFormValidator } from '../validation';
import type { z } from 'zod';
import { useNavigate } from 'react-router';
import AddFacebookPageDialog from './AddFacebookPageDialog';
import { usePopupState } from 'material-ui-popup-state/hooks';
import AddCreativeTextDialog from './AddCreativeTextDialog';
import { usePermissionGuard } from 'features/login';
import { NoAccessField } from 'components/layout';

const SuspenseErrorBoundary: FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <GlobalErrorBoundary FallbackComponent={GridErrorBoundaryFallback}>
      <Suspense
        fallback={
          <Skeleton
            height={40}
            sx={{ transform: 'translateY(0)', borderRadius: '8px' }}
          />
        }
      >
        {children}
      </Suspense>
    </GlobalErrorBoundary>
  );
};

export const ControlFacebookPages = () => {
  const { accessToAddFacebookpage } = usePermissionGuard();
  const { setValue, getValues } = useFormContext();
  const [isLinkMorePages, setLinkMorePages] = useState(false);
  const popupState = usePopupState({
    variant: 'dialog',
    popupId: 'AddFacebookPageDialog',
  });

  const handleLinkMorePages = useCallback(() => {
    setLinkMorePages((prev) => {
      if (prev) {
        setValue('additionalFacebookPagesIds', undefined);
      }
      return !prev;
    });
  }, [setLinkMorePages, setValue]);

  const handleAddNewPage = useCallback(() => {
    popupState.open();
  }, [popupState]);

  const handleOnSuccess = useCallback(
    (data: AddFacebookPageResponse) => {
      setValue('additionalFacebookPagesIds', [
        ...(getValues('additionalFacebookPagesIds') ?? []),
        data.facebook_page_id,
      ]);
      setLinkMorePages(true);
      popupState.close();
    },
    [getValues, popupState, setLinkMorePages, setValue],
  );

  useEffect(() => {
    setLinkMorePages(!!getValues('additionalFacebookPagesIds'));
  }, [setLinkMorePages, getValues]);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}
      >
        <SuspenseErrorBoundary>
          <SelectFacebookPage
            name={'defaultFacebookPageId'}
            inputLabel={'Default Facebook Page'}
            placeholder={'Value'}
            required
          />
        </SuspenseErrorBoundary>
        {isLinkMorePages && (
          <SuspenseErrorBoundary>
            <SelectFacebookPage
              name={'additionalFacebookPagesIds'}
              inputLabel={'Facebook Pages'}
              placeholder={'Value'}
              multiples
            />
          </SuspenseErrorBoundary>
        )}
      </Box>
      <Box>
        <Button
          startIcon={isLinkMorePages ? <Remove /> : <Add />}
          variant={'text'}
          color={isLinkMorePages ? 'secondary' : 'inherit'}
          onClick={handleLinkMorePages}
        >
          {isLinkMorePages
            ? 'Remove additional pages'
            : 'Link additional pages'}
        </Button>
        <Button
          disabled={!accessToAddFacebookpage}
          startIcon={<Add />}
          variant={'text'}
          color={'inherit'}
          onClick={handleAddNewPage}
        >
          Add new page
        </Button>
      </Box>
      {accessToAddFacebookpage && (
        <AddFacebookPageDialog
          popupState={popupState}
          onSuccess={handleOnSuccess}
        />
      )}
    </Box>
  );
};

export const ControlCreativeText = () => {
  const { accessToAddOriginaltextcreative } = usePermissionGuard();
  const { setValue, getValues } = useFormContext();
  const [isLinkMore, setLinkMore] = useState(false);
  const popupState = usePopupState({
    variant: 'dialog',
    popupId: 'AddCreativeTextDialog',
  });

  const handleLinkMore = useCallback(() => {
    setLinkMore((prev) => {
      if (prev) {
        setValue('additionalCreativeTextsIds', undefined);
      }
      return !prev;
    });
  }, [setLinkMore, setValue]);

  const handleAddNewText = useCallback(() => {
    popupState.open();
  }, [popupState]);

  const handleOnSuccess = useCallback(
    (data: CreativeTextResponse) => {
      setValue('additionalCreativeTextsIds', [
        ...(getValues('additionalCreativeTextsIds') ?? []),
        data.creative_text_id,
      ]);
      setLinkMore(true);
      popupState.close();
    },
    [getValues, popupState, setLinkMore, setValue],
  );

  useEffect(() => {
    setLinkMore(!!getValues('additionalCreativeTextsIds'));
  }, [setLinkMore, getValues]);

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}
    >
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}
      >
        <SuspenseErrorBoundary>
          <SelectCreativeTexts
            name={'defaultCreativeTextId'}
            inputLabel={'Default creative text'}
            placeholder={'Value'}
            asAutocomplete
            required
          />
        </SuspenseErrorBoundary>
        {isLinkMore && (
          <SuspenseErrorBoundary>
            <SelectCreativeTexts
              name={'additionalCreativeTextsIds'}
              inputLabel={'Creative Texts'}
              placeholder={'Value'}
              multiples
            />
          </SuspenseErrorBoundary>
        )}
      </Box>
      <Box>
        <Button
          startIcon={isLinkMore ? <Remove /> : <Add />}
          variant={'text'}
          color={isLinkMore ? 'secondary' : 'inherit'}
          onClick={handleLinkMore}
        >
          {isLinkMore ? 'Remove additional text' : 'Link additional text'}
        </Button>
        <Button
          disabled={!accessToAddOriginaltextcreative}
          startIcon={<Add />}
          variant={'text'}
          color={'inherit'}
          onClick={handleAddNewText}
        >
          Add new text
        </Button>
      </Box>
      {accessToAddOriginaltextcreative && (
        <AddCreativeTextDialog
          popupState={popupState}
          onSuccess={handleOnSuccess}
        />
      )}
    </Box>
  );
};

type FormData = z.infer<typeof addFlowFormValidator.schema>;

interface FlowFormProps {
  data?: FlowDetails;
}

const FlowForm: FC<FlowFormProps> = ({ data }) => {
  const {
    accessToViewOriginaltextcreative,
    accessToViewFacebookpage,
    accessToDeleteFlow,
  } = usePermissionGuard();
  const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
  const navigate = useNavigate();
  const { open, close, isOpen } = useModal();
  const isEditMode = !isEmpty(data);

  const addFlow = useAddFlow({
    config: createCabinetMutationTracking({
      platform: 'facebook',
      entity: 'flow',
      mode: 'create',
      onSuccess: () => {
        onShowAlert('Flow added successfully');
        navigate('/cabinet/facebook/flows');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    }),
  });

  const updateFlow = useUpdateFlow({
    config: createCabinetMutationTracking({
      platform: 'facebook',
      entity: 'flow',
      mode: 'edit',
      onSuccess: () => {
        onShowAlert('Flow updated successfully');
        navigate('/cabinet/facebook/flows');
      },
      onError: (error) => {
        onShowErrorAlert(error.message);
      },
    }),
  });

  const formMethods = useForm<FormData>({
    resolver: addFlowFormValidator.resolver,
    defaultValues: {
      flowId: data?.id,
      flowName: data?.name ?? '',
      app: data?.app ?? '',
      platform: data?.platform ?? '',
      url: data?.url ?? '',
      defaultFacebookPageId: data?.default_facebook_page?.id,
      defaultCreativeTextId: data?.default_creative_text?.id,

      additionalFacebookPagesIds: !isEmpty(data?.additional_facebook_pages)
        ? (data?.additional_facebook_pages as FacebookPage[])?.map(
          ({ id }) => id,
        )
        : undefined,

      additionalCreativeTextsIds: !isEmpty(data?.additional_creative_texts)
        ? (data?.additional_creative_texts as CreativeText[])?.map(
          ({ id }) => id,
        )
        : undefined,
    },
  });

  const {
    handleSubmit,
    formState: { errors, isValid },
  } = formMethods;

  useScrollToError<FormData>({ errors });

  const onSubmit = useCallback(
    (data: FormData) => {
      trackCabinetSubmitAttempt(
        'facebook',
        'flow',
        isEditMode ? 'edit' : 'create',
      );

      const payload = {
        name: data?.flowName,
        app: data?.app,
        platform: data?.platform,
        url: data?.url,
        default_facebook_page_id: data.defaultFacebookPageId,
        default_creative_text_id: data.defaultCreativeTextId,
        additional_facebook_pages_ids: data.additionalFacebookPagesIds,
        additional_creative_texts_ids: data.additionalCreativeTextsIds,
      };

      if (isEditMode) {
        updateFlow.mutate({ ...payload, flow_id: Number(data.flowId) });
      } else {
        addFlow.mutate(payload);
      }
    },
    [addFlow, updateFlow, isEditMode],
  );

  const isPending = isEditMode ? updateFlow.isPending : addFlow.isPending;

  return (
    <>
      <FormProvider {...formMethods}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            maxWidth: '40%',
            alignItems: 'start',
            justifyContent: 'start',
          }}
        >
          <Typography variant={'subtitle1'}>
            {isEditMode ? 'Edit flow' : 'Add flow'}
          </Typography>
          <TextField
            name={'flowName'}
            inputLabel={'Flow Name'}
            placeholder={'Value'}
            inputProps={{ autoFocus: true }}
            required
          />
          <SelectApplications required />
          <SuspenseBoundary>
            <SelectPlatform required />
          </SuspenseBoundary>
          <TextField
            name={'url'}
            inputLabel={'URL'}
            placeholder={'Value'}
            required
          />
          {accessToViewFacebookpage ? (
            <SuspenseBoundary
              datadogSource={cabinetDatadogSources.query('facebook', 'facebook_page')}
            >
              <ControlFacebookPages />
            </SuspenseBoundary>
          ) : (
            <NoAccessField text={'No access to view facebook pages'} />
          )}
          {accessToViewOriginaltextcreative ? (
            <SuspenseBoundary
              datadogSource={cabinetDatadogSources.query('facebook', 'creative_text')}
            >
              <ControlCreativeText />
            </SuspenseBoundary>
          ) : (
            <NoAccessField text={'No access to view creative texts'} />
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Button
              startIcon={
                isPending && <CircularProgress color={'inherit'} size={18} />
              }
              onClick={handleSubmit(onSubmit)}
              variant={'contained'}
              disabled={isPending || !isValid}
            >
              {isEditMode ? 'Update flow' : 'Save flow'}
            </Button>
            {isEditMode && data && (
              <>
                <Button
                  variant={'outlined'}
                  color={'error'}
                  onClick={open}
                  disabled={isPending || !accessToDeleteFlow}
                >
                  Delete
                </Button>
                {accessToDeleteFlow && (
                  <DeleteFlow
                    isOpen={isOpen}
                    close={close}
                    id={data.id}
                    name={data.name}
                    open={open}
                  />
                )}
              </>
            )}
          </Box>
        </Box>
        <ConfirmationDialog
          isUnblock={addFlow.isSuccess || updateFlow.isSuccess}
        />
      </FormProvider>
    </>
  );
};

export default FlowForm;
